import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  createTheme,
  ThemeProvider,
  Avatar,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import { DriversSalary } from "../../../Data/axios";
import config from "../../../Data/AuthUser";
import StatusAlert from "../../../StatusAlertList/StatusAlert";

const DEPOSIT_LIMIT_DAYS = 15;

const RemarkEditor = ({ value, onSave }) => {
  const [draft, setDraft] = useState(value || "");
  return (
    <TextField
      type="text"
      value={draft}
      size="small"
      placeholder="Released / held / ..."
      onChange={(e) => setDraft(e.target.value)}
      onBlur={(e) => {
        if (e.target.value !== (value || "")) onSave(e.target.value);
      }}
      sx={{
        width: '220px', backgroundColor: 'white',
        '& .MuiInputBase-input': { color: 'black' },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'gray' },
      }}
    />
  );
};

const GetInitials = (name) => {
  if (!name) return "NA";
  const words = name.trim().split(" ");
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

const AvCell = ({ name, id, image, color }) => {
  const [imageError, setImageError] = useState(false);
  const imageUrl = image && image !== 'non' && !imageError ? `${config.apiImageUrl}${image}` : null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <Avatar
        src={imageUrl}
        alt={name}
        onError={() => setImageError(true)}
        sx={{ width: 30, height: 30, backgroundColor: color, color: "white", fontWeight: "bold", fontSize: "12px" }}
      >
        {GetInitials(name)}
      </Avatar>
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <Typography variant="body2" fontWeight="bold" color="black" sx={{ lineHeight: '1.2', marginBottom: '2px' }}>
          {name}
        </Typography>
        <Typography variant="caption" color="gray" sx={{ lineHeight: '1.2' }}>
          {id}
        </Typography>
      </div>
    </div>
  );
};

const toDMY = (d) => (d ? String(d).split("-").reverse().join("/") : "");

const DriverDepositTransactions = ({ clientId: propClientId, filters = {}, initiatedLabel = "Initiated" }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 5, count: 0, totalPages: 0 });

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");

  const showAlert = (message, severity) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const fetchDeposits = useCallback(async (page = 0, rowsPerPage = 5) => {
    setLoading(true);
    try {
      const params = { page: page + 1, limit: rowsPerPage, deposit_only: 1 };
      if (propClientId) params.client_id = propClientId;
      if (filters.name) params.name = filters.name;
      if (filters.status !== "" && filters.status !== undefined) params.deposit_status = filters.status;
      const fFrom = filters.from || "";
      const fTo = filters.to || "";
      if (fFrom || fTo) params.timestamps = fFrom && fTo ? `${fFrom},${fTo}` : (fFrom || fTo);

      const response = await DriversSalary.getDriverSalary(params);
      if (response.success) {
        setRows(response.data || []);
        setPagination((prev) => ({
          ...prev,
          count: response.pagination?.total_records || 0,
          totalPages: response.pagination?.total_pages || 0,
        }));
      } else {
        showAlert(response.message || "Failed to fetch deposits", "error");
      }
    } catch (error) {
      console.error("Error fetching deposits:", error);
      showAlert("An error occurred while fetching deposits", "error");
    } finally {
      setLoading(false);
    }
  }, [propClientId, filters.name, filters.status, filters.from, filters.to]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    fetchDeposits(0, pagination.rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDeposits]);

  const handleStatusChange = async (rowId, status) => {
    if (!rowId) return;
    const response = await DriversSalary.updateDriverDeposit(rowId, { deposit_status: status });
    if (response.success) {
      showAlert("Deposit status updated", "success");
      fetchDeposits(pagination.page, pagination.rowsPerPage);
    } else {
      showAlert(response.message || "Failed to update status", "error");
    }
  };

  const handleRemarkSave = async (rowId, remark) => {
    if (!rowId) return;
    const response = await DriversSalary.updateDriverDeposit(rowId, { deposit_remark: remark });
    if (response.success) {
      showAlert("Deposit remarks updated", "success");
      fetchDeposits(pagination.page, pagination.rowsPerPage);
    } else {
      showAlert(response.message || "Failed to update remarks", "error");
    }
  };

  const getStatusColor = (status) => {
    switch (String(status)) {
      case "0": return "#ff9800";
      case "1": return "#2196f3";
      case "2": return "#4caf50";
      default: return "#ff9800";
    }
  };

  const getTheme = () =>
    createTheme({
      components: {
        MuiTableCell: {
          styleOverrides: {
            head: { backgroundColor: "#FBC02D", color: "black", fontWeight: "bold", textAlign: "center", verticalAlign: "middle" },
            body: { color: "black", backgroundColor: "white", verticalAlign: "middle" },
            root: { backgroundColor: "white" },
          },
        },
        MUIDataTableHeadCell: { styleOverrides: { root: { backgroundColor: "#FBC02D", color: "black" } } },
        MuiTableBody: { styleOverrides: { root: { backgroundColor: "white" } } },
      },
    });

  const columns = [
    {
      name: "driver", label: "Driver",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          return row ? <AvCell name={row.user_username} id={row.user_id} image={row.user_image} color="#FBC02D" /> : value;
        },
      },
    },
    {
      name: "client", label: "Client",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          return row ? <AvCell name={row.company_name || row.client_username} id={row.client_id} image={row.client_image} color="#2196f3" /> : value;
        },
      },
    },
    {
      name: "billing", label: "Billing Dates",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          return row ? (
            <Typography variant="body2" color="black">{toDMY(row.from)} — {toDMY(row.till)}</Typography>
          ) : value;
        },
      },
    },
    {
      name: "amount", label: "Deposit Amount",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          return row ? (
            <Typography variant="body2" fontWeight="bold" color="black">
              ₹{parseFloat(row.deposit_amount || 0).toFixed(2)}
              <Typography variant="caption" color="gray" display="block">{row.deposit_days}d held</Typography>
            </Typography>
          ) : value;
        },
      },
    },
    {
      name: "status", label: "Status",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          if (!row) return value;
          return (
            <Select
              value={String(row.deposit_status ?? 0)}
              onChange={(e) => handleStatusChange(row.id, parseInt(e.target.value))}
              size="small"
              sx={{
                width: "auto", minWidth: "110px", color: getStatusColor(row.deposit_status),
                fontWeight: "bold", backgroundColor: "white",
                '& .MuiOutlinedInput-notchedOutline': { borderColor: getStatusColor(row.deposit_status) },
              }}
            >
              <MenuItem value="0">{initiatedLabel}</MenuItem>
              <MenuItem value="1">Processed</MenuItem>
              <MenuItem value="2">Settled / Released</MenuItem>
            </Select>
          );
        },
      },
    },
    {
      name: "remark", label: "Deposit Remarks",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          if (!row) return value;
          return <RemarkEditor value={row.deposit_remark} onSave={(remark) => handleRemarkSave(row.id, remark)} />;
        },
      },
    },
    {
      name: "note", label: "Note",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          return row ? <Typography variant="body2" color="textSecondary">{row.note || "N/A"}</Typography> : value;
        },
      },
    },
  ];

  const options = {
    selectableRows: "none",
    filter: false,
    viewColumns: false,
    search: false,
    download: false,
    print: false,
    pagination: true,
    elevation: 0,
    responsive: "scrollMaxHeight",
    scrollMaxHeight: "calc(100vh - 300px)",
    count: pagination.count,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    rowsPerPageOptions: [5, 10, 25, 50, 100],
    onChangePage: (page) => {
      setPagination((prev) => ({ ...prev, page }));
      fetchDeposits(page, pagination.rowsPerPage);
    },
    onChangeRowsPerPage: (rowsPerPage) => {
      setPagination((prev) => ({ ...prev, rowsPerPage, page: 0 }));
      fetchDeposits(0, rowsPerPage);
    },
    textLabels: {
      body: { noMatch: loading ? "Loading..." : "No deposits found" },
    },
  };

  return (
    <>
      <StatusAlert open={alertOpen} setOpen={setAlertOpen} message={alertMessage} severity={alertSeverity} />

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" color="black" sx={{ fontWeight: 'bold' }}>
          Total Deposits: {pagination.count}
        </Typography>
        <Typography variant="body2" color="black">•</Typography>
        <Typography variant="body2" color="black">
          Page {pagination.page + 1} of {pagination.totalPages}
        </Typography>
        <Typography variant="body2" color="black" sx={{ ml: 2 }}>
          Hold: {DEPOSIT_LIMIT_DAYS} present days — first assignment only, within 45 days
        </Typography>
      </Box>

      {loading && rows.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ThemeProvider theme={getTheme()}>
          <MUIDataTable
            title={
              <Typography variant="h5" fontWeight="bold" color="black">
                Credit Note — Driver Deposits
              </Typography>
            }
            data={rows}
            columns={columns}
            options={options}
          />
        </ThemeProvider>
      )}
    </>
  );
};

export default DriverDepositTransactions;